<script>
import BreadcrumbsWrapper from '@components/breadcrumbs/breadcrumbs-wrapper.vue';
import CardRecipe from '@components/recipe/card-recipe.vue';
import NoSearchResults from '@components/notifications/no-search-results.vue';
import SearchInput from '@components/search-input/search-input.vue';
import Sidebar from '@components/sidebar/sidebar.vue';
import { gridMixin } from '@mixins/grid-mixin';
import {
  authComputed,
  platelistComputed,
  recipeComputed,
  recipeMethods,
} from '@state/helpers';

export default {
  components: {
    BreadcrumbsWrapper,
    CardRecipe,
    NoSearchResults,
    SearchInput,
    Sidebar,
  },
  props: {},
  mixins: [gridMixin],
  data() {
    return {
      isSortingEnabled: false,
      searchComponentKey: 0,
      searchOpts: { keys: ['title'] },
      searchVal: null,
      showFilters: false,
      tabIndex: 0,
      visibleOptsCount: 5,
      visibleDietOptsCount: null,
      visibleHealthOptsCount: null,
      visibleTagOptsCount: null,
    };
  },
  computed: {
    ...authComputed,
    ...recipeComputed,
    ...platelistComputed,
    recipes() {
      return (
        this.allRecipes &&
        this.allRecipes.hits.filter(
          (recipe) => !recipe.draft && !recipe.archived
        )
      );
    },
    resultsCount() {
      return this.recipes && this.recipes.length;
    },
    leftovers() {
      return this.adminPlanWeek && this.adminPlanWeek.allLeftoverOptions;
    },
    recipeMealtimeOpts() {
      return (
        this.recipeFilterOpts &&
        this.recipeFilterOpts['Meal Time Requirements'] &&
        this.recipeFilterOpts['Meal Time Requirements'].options
      );
    },
    recipeDietOpts() {
      return (
        this.recipeFilterOpts &&
        this.recipeFilterOpts['Diet Requirements'] &&
        this.recipeFilterOpts['Diet Requirements'].options
      );
    },
    recipeHealthOpts() {
      return (
        this.recipeFilterOpts &&
        this.recipeFilterOpts['Health Requirements'] &&
        this.recipeFilterOpts['Health Requirements'].options
      );
    },
    recipeTypeOpts() {
      return (
        this.recipeFilterOpts &&
        this.recipeFilterOpts['Recipe Type'] &&
        this.recipeFilterOpts['Recipe Type'].options
      );
    },
    recipeTagOpts() {
      return (
        this.recipeFilterOpts &&
        this.recipeFilterOpts['Tag Requirements'] &&
        this.recipeFilterOpts['Tag Requirements'].options
      );
    },
  },
  beforeMount() {
    this.visibleDietOptsCount = this.visibleOptsCount;
    this.visibleHealthOptsCount = this.visibleOptsCount;
    this.visibleTagOptsCount = this.visibleOptsCount;
  },
  destroyed() {
    this.clearFilters();
  },
  methods: {
    ...recipeMethods,
    onViewRecipe(recipe) {
      this.$emit('view-recipe-details', recipe);
    },
    onSelectRecipe(recipe) {
      this.$emit('select-recipe', recipe);
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    optsCount(group) {
      switch (group) {
        case 'dietOpts':
          return Object.keys(this.recipeDietOpts).length;
        case 'healthOpts':
          return Object.keys(this.recipeHealthOpts).length;
        case 'tagOpts':
          return Object.keys(this.recipeTagOpts).length;
      }
    },
    showAllOpts(group) {
      switch (group) {
        case 'dietOpts':
          this.visibleDietOptsCount === this.visibleOptsCount
            ? (this.visibleDietOptsCount = this.optsCount(group))
            : (this.visibleDietOptsCount = this.visibleOptsCount);
          break;
        case 'healthOpts':
          this.visibleHealthOptsCount === this.visibleOptsCount
            ? (this.visibleHealthOptsCount = this.optsCount(group))
            : (this.visibleHealthOptsCount = this.visibleOptsCount);
          break;
        case 'tagOpts':
          this.visibleTagOptsCount === this.visibleOptsCount
            ? (this.visibleTagOptsCount = this.optsCount(group))
            : (this.visibleTagOptsCount = this.visibleOptsCount);
          break;
        default:
          break;
      }
    },
    async onSelectFilter(opt, group) {
      this.setIsLoadingRecipes(true);
      await this.setSelectedRecipeFilterOpts({ opt, group });
      await this.getAllRecipes();
      this.setIsLoadingRecipes(false);
    },
    onFilterResults(val) {
      this.setAllRecipes({ ...this.allRecipes, hits: val });
    },
    async clearFilters() {
      this.setSelectedRecipeFilterOpts('reset');
      this.searchComponentKey++;
      this.setClearRecipeFilters(true);
      this.setIsLoadingRecipes(true);
      await this.getAllRecipes();
      this.setIsLoadingRecipes(false);
      if (this.$refs && this.$refs.checkBox) {
        this.$refs.checkBox.forEach((checkBox) => {
          checkBox.$el.children[0].firstChild.checked = false;
        });
      }
    },
  },
};
</script>

<template>
  <div class="recipe-library">
    <BreadcrumbsWrapper prevPageName="Meal Plans" />

    <div class="recipe-filters">
      <Sidebar :class="{ visible: showFilters }" type="light">
        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-close'" v-bind:svg-inline="''" @click="toggleFilters" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.395 2.839L16.274.718 9.556 7.435 2.84.718.718 2.838l6.717 6.718-6.717 6.718 2.12 2.121 6.718-6.717 6.718 6.717 2.121-2.121-6.717-6.718 6.717-6.717z" fill="#030C16"/></svg>
        <div class="sidebar-section">
          <legend class="sidebar-section-title">Meal Times</legend>
          <div class="sidebar-section-container">
            <BaseCheckbox
              ref="checkBox"
              v-for="(item, i) in recipeMealtimeOpts"
              :item="item"
              :itemIndex="i"
              :key="i"
              :style="
                `display: ${
                  parseInt(i) + 1 > visibleDietOptsCount
                    ? 'none'
                    : 'inline-flex'
                }`
              "
              @change="onSelectFilter(item, 'Meal Time Requirements')"
            />
          </div>
        </div>

        <div class="sidebar-section">
          <legend class="sidebar-section-title">Diets</legend>
          <div class="sidebar-section-container">
            <BaseCheckbox
              ref="checkBox"
              v-for="(item, i) in recipeDietOpts"
              :item="item"
              :itemIndex="i"
              :key="i"
              :style="
                `display: ${
                  parseInt(i) + 1 > visibleDietOptsCount
                    ? 'none'
                    : 'inline-flex'
                }`
              "
              @change="onSelectFilter(item, 'Diet Requirements')"
            />
          </div>
          <a
            class="link-more"
            v-if="optsCount('dietOpts') > visibleOptsCount"
            @click="showAllOpts('dietOpts')"
          >
            <span v-if="visibleDietOptsCount === visibleOptsCount"
              >More...</span
            >
            <span v-else>Less...</span>
          </a>
        </div>

        <div class="sidebar-section">
          <legend class="sidebar-section-title">Lifestyles</legend>
          <div class="sidebar-section-container">
            <BaseCheckbox
              ref="checkBox"
              v-for="(item, i) in recipeHealthOpts"
              :item="item"
              :itemIndex="i"
              :key="i"
              :style="
                `display: ${
                  parseInt(i) + 1 > visibleHealthOptsCount
                    ? 'none'
                    : 'inline-flex'
                }`
              "
              @change="onSelectFilter(item, 'Health Requirements')"
            />
          </div>
          <a
            class="link-more"
            v-if="optsCount('healthOpts') > visibleOptsCount"
            @click="showAllOpts('healthOpts')"
          >
            <span v-if="visibleHealthOptsCount === visibleOptsCount"
              >More...</span
            >
            <span v-else>Less...</span>
          </a>
        </div>

        <div class="sidebar-section">
          <legend class="sidebar-section-title">Common Tags</legend>
          <div class="sidebar-section-container">
            <BaseCheckbox
              ref="checkBox"
              v-for="(item, i) in recipeTagOpts"
              :item="item"
              :itemIndex="i"
              :key="i"
              @change="onSelectFilter(item, 'Tag Requirements')"
            />
          </div>
        </div>
      </Sidebar>
    </div>

    <b-tabs content-class="mt-4" v-model="tabIndex">
      <b-tab title="Recipe Library" active>
        <div class="recipe-library-header">
          <SearchInput
            :key="searchComponentKey"
            :dark="true"
            :list="allRecipes.hits"
            :options="searchOpts"
            width="full"
            @filter-results="onFilterResults"
          />
          <div class="filter-wrapper">
            <span class="result-count">{{ resultsCount }} Results</span>
            <span class="btn-filter" v-if="isSortingEnabled">
              Sort <img src="@assets/images/icon-sort.svg" />
            </span>
            <span
              :class="['btn-filter', { active: showFilters }]"
              @click="toggleFilters"
              >Filter
              <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-filter'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.208 5.792c-1.32 0-2.43-.903-2.744-2.125H.417V2.25h5.047a2.835 2.835 0 112.744 3.542zm4.25-3.542h2.125v1.417h-2.125V2.25zM4.667 12.875c-1.32 0-2.43-.903-2.744-2.125H.417V9.333h1.506a2.835 2.835 0 112.744 3.542zm4.25-2.125h5.666V9.333H8.917v1.417zm-2.834-.708a1.417 1.417 0 11-2.833 0 1.417 1.417 0 012.833 0zm3.542-7.084a1.417 1.417 0 11-2.833 0 1.417 1.417 0 012.833 0z" fill="#000"/></svg></span>
          </div>
        </div>

        <div class="loading-wrapper" v-if="isLoadingRecipes">
          <BaseLoadingSpinner />
        </div>

        <NoSearchResults
          v-if="!isLoadingRecipes && recipes && !recipes.length"
          text="This recipe does not exist in the recipes Library. If you want to use this recipe, you will need create it."
          @clear-search-filters="clearFilters"
        />

        <div
          class="recipe-library-grid"
          v-if="!isLoadingRecipes && recipes && recipes.length"
        >
          <div class="recipe-list-wrapper">
            <CardRecipe
              v-for="(item, i) in recipes"
              :key="i"
              :recipe="item"
              :isLibraryRecipe="true"
              @view-recipe-details="onViewRecipe"
              @select-recipe="onSelectRecipe"
            />
          </div>
        </div>
      </b-tab>
      <b-tab title="Leftovers" @click="tabIndex = 1">
        <div class="recipe-library-grid">
          <div class="recipe-list-wrapper">
            <CardRecipe
              v-for="(item, i) in leftovers"
              :key="i"
              :recipe="item"
              :isLeftover="true"
              :isLibraryRecipe="true"
              @view-recipe-details="onViewRecipe"
              @select-recipe="onSelectRecipe"
            />
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.recipe-library {
  border-right: 1px solid $color-light-gray;
  height: 100vh;
  overflow-y: scroll;
  width: 40.6rem;

  .breadcrumb-wrapper {
    border-bottom: 1px solid $color-light-gray;
    margin-bottom: 0;
    padding: 1.4rem 1.7rem 0.7rem;
  }

  .sidebar {
    box-shadow: 0 2.5rem 1.8rem rgba($color-black-wo-rgb, 0.25);
    padding-top: 0;
    position: absolute;
    top: 26.5rem;
    transform: translateX(-105%);
    transition: transform $duration-animation-base ease-in-out;
    width: 20.2rem;
    z-index: 1;

    &.visible {
      transform: translateX(0);
    }

    .icon-close {
      left: 14rem;
      position: sticky;
      top: 1.8rem;
      z-index: 1;

      &:hover {
        cursor: pointer;
      }
    }

    .sidebar-section-container {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      justify-content: center;

      a {
        color: $color-gray;
        font-size: 1.1rem;
        margin-left: 2.4rem;
      }
    }

    .base-checkbox {
      display: block;
    }

    .sidebar-section:last-of-type {
      margin-bottom: 20rem;
    }
  }

  .recipe-library-header {
    padding: 1rem 2rem;

    .filter-wrapper {
      align-items: center;
      display: flex;
      justify-content: space-between;
      padding: 2.9rem 0 1.2rem;

      .result-count {
        font-size: 1.2rem;
        font-weight: normal;
        line-height: 1.6rem;
      }

      .btn-filter {
        background-color: $color-off-white;
        border-radius: $base-border-radius;
        color: black;
        display: flex;
        font-size: 14px;
        font-weight: normal;
        justify-content: space-between;
        line-height: 19px;
        padding: 0.3rem 0.8rem;
        width: 9.3rem;

        &.active,
        &:hover {
          background-color: $color-action;
          color: white;
          cursor: pointer;

          .icon-filter {
            path {
              fill: white;
            }
          }
        }
      }
    }
  }

  .loading-wrapper {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-bottom: 30vh;
    margin-top: 10vh;
    width: 100%;

    ::v-deep .loading {
      height: 4rem;
      width: 4rem;
    }
  }

  ::v-deep .empty-state-container {
    margin: 0 auto;
    padding: 2rem 1rem;
    width: 95%;

    .actions {
      flex-direction: column;
      justify-content: center;
    }

    .button.btn-secondary {
      margin-bottom: 1.2rem;
      margin-right: 0;
    }
  }

  .recipe-library-grid {
    padding: 0 0.5rem;

    .recipe-list-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      ::v-deep .card-recipe-content {
        .list-macros li {
          line-height: 1.8rem;
          padding: 0 1rem 0 1.2rem;

          .notification-bubble {
            border: 0;
            height: 0.543rem;
            top: 0.6rem;
            width: 0.543rem;
          }
        }
      }

      .card-recipe-container {
        margin-bottom: 1rem;
        margin-right: 0 !important;
        padding: 0 0.125rem;
        width: 100%;

        &:hover {
          border-radius: $base-border-radius-sm;
          box-shadow: 0 0.2rem 1.8rem rgba($color-black-wo-rgb, 0.25);
          padding: 0;
        }

        &:focus,
        &:active {
          box-shadow: none;
        }
      }
    }
  }

  ::v-deep .tabs {
    position: relative;

    > div:first-of-type {
      background: white;
      padding-bottom: 1rem;
      position: sticky;
      top: 0;
      z-index: 1;
    }
  }

  ::v-deep .nav-tabs {
    border: 0;

    .nav-item {
      background-color: $color-off-white;
      flex: 1;
      justify-content: center;
    }

    a.nav-link {
      border: 0;
      color: $color-heading-text;
      font-size: 1.4rem;
      font-style: normal;
      font-weight: normal;
      line-height: 1.9rem;
      padding: 1.4rem 1rem;
      text-align: center;
      text-decoration: none;

      &.active {
        border: 0;
        color: $color-heading-text;
      }
    }
  }
}
</style>
